import { CommonModule } from '@angular/common';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Dictionary } from '@ngrx/entity';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { debounceTime, take } from 'rxjs';
import { IntelligenceModels } from '@models/intelligence.model';
import {
  AlertCategory,
  EventCameraSyncMap,
  EventV2Document,
  FlowLookup,
  IntegrationsType,
  StatusType,
} from '@models/alerts-v2.model';
import { AlertSyncStatus } from '@enums/alert-events.enum';
import {
  ConfirmDialogModel,
  ConfirmDialogService,
  PreloaderColor,
  UiCameraSelectButtonComponent,
  UiFilterChipComponent,
  UiPreloaderComponent,
  UiToggleComponent,
} from '@ui';
import { SharedActions } from '@states/shared/shared.action-types';
import { CameraSelectors } from '@states/camera/camera.selector-types';
import { DatabaseTagsActions } from '@states/database-tags/database-tags.action.types';
import { DatabaseTagsSelectors } from '@states/database-tags/database-tags.selector-types';
import { OrganizationSelectors } from '@states/organization/organization.selector-types';
import { DoorsActions } from '@states/doors/doors.action-types';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import * as AlarmActions from '@states/alarm/alarm.actions';
import { routerSegments } from '@consts/routes';
import { IntelligenceResultsService } from '../../../../../../services/intelligence-results.service';
import { IntelligenceToolsService } from '../../../../../../services/intelligence-tools.service';
import { UtilsService } from '../../../../../../edge/utils.service';
import { AppState } from '../../../../../../store/app.state';
import { generateCameraIdsIncludesExcludes } from '../../../../../../helpers/common.helpers';
import { CameraPickerModalComponent } from '../../../../../../modals/camera-picker-modal/camera-picker-modal.component';
import { CameraPickerModels } from '../../../../../../modals/camera-picker-inline/models/models';
import { EventCameraSnapshotComponent } from '../../../../../../pages/alerts-v2/pages/alert-main-v2/components/event-camera-snapshot/event-camera-snapshot.component';
import { StepOptionSelectorModule } from '../../../../../../pages/alerts-v2/components/step-option-selector/step-option-selector.module';
import { EventSentenceRendererComponent } from '../../../../../components/event-sentence-renderer/event-sentence-renderer.component';
import { StatusChipComponent } from '../../../../../status-chip/status-chip.component';
import { UiSearchComponent } from '../../../../../ui-kit/ui-search/ui-search.component';
import { TagMenuComponent } from '../../../../../../pages/database/pages/tags/components/tag-menu/tag-menu.component';
import { TagFilterComponent } from '../../../../../../pages/database/pages/tags/components/tag-filter/tag-filter.component';
import {
  buildAlertTypeGroupsForSelector,
  buildAlertTypeKeyValuePairs,
  fromFlowTypeKey,
  toFlowTypeKey,
} from '../alert-type-filter.util';

@Component({
  selector: 'app-alert-configurations-result',
  standalone: true,
  imports: [
    CommonModule,
    ScrollingModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatTooltip,
    MatMenuModule,
    UiCameraSelectButtonComponent,
    UiFilterChipComponent,
    UiPreloaderComponent,
    UiSearchComponent,
    UiToggleComponent,
    StepOptionSelectorModule,
    TagFilterComponent,
    EventCameraSnapshotComponent,
    EventSentenceRendererComponent,
    StatusChipComponent,
    TagMenuComponent,
  ],
  templateUrl: './alert-configurations-result.component.html',
  styleUrls: ['./alert-configurations-result.component.scss'],
})
export class AlertConfigurationsResultComponent implements OnInit {
  private readonly resultsService = inject(IntelligenceResultsService);
  private readonly toolsService = inject(IntelligenceToolsService);
  private readonly store$ = inject<Store<AppState>>(Store);
  private readonly confirmDialog = inject(ConfirmDialogService);
  private readonly utilsService = inject(UtilsService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly PreloaderColor = PreloaderColor;
  protected readonly AlertSyncStatus = AlertSyncStatus;

  protected readonly deletingId = signal<string | null>(null);
  protected readonly togglingId = signal<string | null>(null);
  protected readonly editingId = signal<string | null>(null);
  protected readonly isFiltering = signal<boolean>(false);

  protected readonly searchQuery = signal<string>('');
  protected readonly selectedFlowTypes = signal<FlowLookup[]>([]);
  protected readonly selectedTags = signal<string[]>([]);
  protected readonly selectedCameras = signal<
    Dictionary<CameraPickerModels.SelectedCamera>
  >({});
  protected readonly tagsFilterControl = new FormControl<string[]>([], {
    nonNullable: true,
  });

  protected readonly camerasEntities = this.store$.selectSignal(
    CameraSelectors.selectAllCamerasEntities,
  );
  private readonly allCameras = this.store$.selectSignal(
    CameraSelectors.selectAllCameras,
  );
  protected readonly tagEntities = this.store$.selectSignal(
    DatabaseTagsSelectors.selectDatabaseTagEntities,
  );
  private readonly enablePalestinianLicensePlate = this.store$.selectSignal(
    OrganizationSelectors.selectEnablePalestinianLicensePlate,
  );
  private readonly disableMissingObjectEventConfiguration = this.store$.selectSignal(
    OrganizationSelectors.selectDisableMissingObjectEventConfiguration,
  );
  private readonly isDeveloper = this.store$.selectSignal(OrganizationSelectors.isDeveloper);

  protected readonly alertTypeGroupsForSelector = computed(() =>
    buildAlertTypeGroupsForSelector(
      {
        enablePalestinianLicensePlate: this.enablePalestinianLicensePlate(),
        disableMissingObjectEventConfiguration: this.disableMissingObjectEventConfiguration(),
      },
      this.isDeveloper(),
    ),
  );
  protected readonly alertTypeKeyValuePairs = computed(() =>
    buildAlertTypeKeyValuePairs(
      {
        enablePalestinianLicensePlate: this.enablePalestinianLicensePlate(),
        disableMissingObjectEventConfiguration: this.disableMissingObjectEventConfiguration(),
      },
      this.isDeveloper(),
    ),
  );

  protected readonly selectedFlowTypeKeys = computed<string[]>(() =>
    this.selectedFlowTypes().map((flowType) =>
      toFlowTypeKey(flowType.category, flowType.flowType),
    ),
  );
  protected readonly selectedCameraList = computed<
    CameraPickerModels.SelectedCamera[]
  >(() =>
    Object.values(this.selectedCameras()).filter(
      (camera): camera is CameraPickerModels.SelectedCamera => !!camera,
    ),
  );
  protected readonly hasActiveFilters = computed<boolean>(
    () =>
      !!this.searchQuery() ||
      this.selectedFlowTypes().length > 0 ||
      this.selectedTags().length > 0 ||
      this.selectedCameraList().length > 0,
  );

  protected readonly response =
    computed<IntelligenceModels.AlertConfigurations.Response | null>(() => {
      const result = this.resultsService.current();
      if (result?.type !== 'alert-configurations') return null;
      return result.data as IntelligenceModels.AlertConfigurations.Response;
    });

  protected readonly configs = computed<
    IntelligenceModels.AlertConfigurations.Item[]
  >(() => this.response()?.items ?? []);
  protected readonly count = computed<number>(
    () => this.response()?.count ?? 0,
  );
  private readonly viewport = viewChild(CdkVirtualScrollViewport);

  private lastEntryPosition = this.resultsService.currentPosition();
  private filterRequestId = 0;
  private editRequestId = 0;
  private readonly resetOnNavigation = effect(() => {
    const position = this.resultsService.currentPosition();
    if (position === this.lastEntryPosition) return;
    this.lastEntryPosition = position;
    this.resetFilters();
    this.showResultQuery();
    // The entry navigated to may carry its own filter state — either the user's last
    // one or the camera/type/tag filter a prompt applied — so restore it over the reset.
    this.restoreUiState();
  });

  public ngOnInit(): void {
    // The event-sentence-renderer resolves schedules, custom events, and doors from
    // the store — populate them the same way the alerts page does so sentences render
    // fully. These are idempotent gets (no-ops when already loaded).
    this.store$.dispatch(DoorsActions.getDoors());
    this.store$.dispatch(CustomEventsActions.getCustomEvents());
    this.store$.dispatch(AlarmActions.getScheduleList());

    if (!Object.keys(this.tagEntities()).length) {
      this.store$.dispatch(DatabaseTagsActions.getDatabaseTags());
    }

    // The entry usually exists before this component does (a tool pushed it), so the
    // navigation effect never fires for it — seed the search bar here too. A stored
    // uiState is the user's own last state and wins over the entry's filter.
    this.showResultQuery();
    this.restoreUiState();

    this.tagsFilterControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((tags) => {
        this.selectedTags.set(tags);
        void this.reload();
      });
  }

  protected openAlertConfigurationsPage(): void {
    const tree = this.router.createUrlTree([
      '/',
      routerSegments.alertsV2,
      routerSegments.list,
    ]);
    const path = this.router.serializeUrl(tree);
    const url = window.location?.origin
      ? `${window.location.origin}${path}`
      : path;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  protected openAlertTemplates(): void {
    if (!this.response()) return;

    this.resultsService.setOverlay(
      { template: null },
      'alert-templates',
      this.translateService.instant(
        'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.CREATE_ALERT',
      ),
    );
  }

  // ── Filters ──────────────────────────────────────────────────────────────────────────────

  protected onSearchQuery(value: string): void {
    const next = value ?? '';
    if (next === this.searchQuery()) return;
    this.searchQuery.set(next);
    void this.reload();
  }

  protected openSelectCameraModal(): void {
    const data: CameraPickerModels.CameraPickerDialogOptions = {
      options: { multi: true },
      selectedValue: this.selectedCameras(),
    };
    this.dialog
      .open(CameraPickerModalComponent, {
        width: '600px',
        height: '70vh',
        panelClass: 'modal-no-padding',
        disableClose: true,
        data,
      })
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((cameras?: Dictionary<CameraPickerModels.SelectedCamera>) => {
        if (!cameras) return;
        this.selectedCameras.set(cameras);
        void this.reload();
      });
  }

  protected onFlowTypesKeysChange(keys: string[]): void {
    const flowTypes = (keys ?? [])
      .map((key) => fromFlowTypeKey(key))
      .filter((flowType): flowType is FlowLookup => !!flowType);
    this.selectedFlowTypes.set(flowTypes);
    void this.reload();
  }

  protected onRemoveSearch(): void {
    this.searchQuery.set('');
    void this.reload();
  }

  protected onRemoveCamera(cameraId: string): void {
    this.selectedCameras.update((cameras) => {
      const next = { ...cameras };
      delete next[cameraId];
      return next;
    });
    void this.reload();
  }

  protected onRemoveFlowType(flowType: FlowLookup): void {
    this.selectedFlowTypes.update((flowTypes) =>
      flowTypes.filter(
        (selected) =>
          selected.category !== flowType.category ||
          selected.flowType !== flowType.flowType,
      ),
    );
    void this.reload();
  }

  protected onRemoveTag(tagId: string): void {
    this.tagsFilterControl.setValue(
      this.tagsFilterControl.value.filter(
        (selectedTagId) => selectedTagId !== tagId,
      ),
    );
  }

  protected flowName(category: number, flowType: number): string | null {
    return this.alertTypeKeyValuePairs()[toFlowTypeKey(category, flowType)] ?? null;
  }

  private async reload(): Promise<void> {
    const requestId = ++this.filterRequestId;
    const entryPosition = this.resultsService.currentPosition();
    const camera = this.buildCameraFilter();
    const params: IntelligenceModels.AlertConfigurations.ListParams = {
      limit:
        this.response()?.filter.limit ??
        IntelligenceModels.AlertConfigurations.DEFAULT_LIMIT,
      query: this.searchQuery() || undefined,
      cameraIdsIncludes: camera.cameraIdsIncludes.length
        ? camera.cameraIdsIncludes
        : undefined,
      cameraIdsExcludes: camera.cameraIdsExcludes.length
        ? camera.cameraIdsExcludes
        : undefined,
      tagIds: this.selectedTags().length ? this.selectedTags() : undefined,
      flowTypes: this.selectedFlowTypes().length
        ? this.selectedFlowTypes()
        : undefined,
    };

    this.isFiltering.set(true);
    try {
      const response = await this.toolsService.fetchAlertConfigurations(params);
      if (
        requestId !== this.filterRequestId ||
        entryPosition !== this.resultsService.currentPosition()
      ) {
        return;
      }
      this.resultsService.replaceAlertConfigurationsInCurrent({
        ...response,
        uiState: this.captureUiState(),
      });
    } catch (err) {
      if (requestId === this.filterRequestId) {
        this.store$.dispatch(
          SharedActions.showMessage({
            error: this.utilsService.errMessage(err as Error),
          }),
        );
      }
    } finally {
      if (requestId === this.filterRequestId) {
        this.isFiltering.set(false);
      }
    }
  }

  private buildCameraFilter(): {
    cameraIdsIncludes: string[];
    cameraIdsExcludes: string[];
  } {
    const selectedCameras = this.selectedCameras();
    if (!Object.keys(selectedCameras).length) {
      return { cameraIdsIncludes: [], cameraIdsExcludes: [] };
    }
    const allowedCameraIds = this.allCameras()
      .map((camera) => camera.edgeOnly?.cameraId)
      .filter((cameraId): cameraId is string => !!cameraId);
    return generateCameraIdsIncludesExcludes(selectedCameras, allowedCameraIds);
  }

  /**
   * A tool-driven entry can arrive already filtered by name (list-alert-configurations
   * with a query, or set-alert-configuration-enabled targeting one name) — show that
   * term so the search bar and the listed rows agree, and clearing it reloads the
   * unfiltered list.
   */
  private showResultQuery(): void {
    this.searchQuery.set(this.response()?.filter?.query ?? '');
  }

  private resetFilters(): void {
    this.filterRequestId++;
    this.editRequestId++;
    this.isFiltering.set(false);
    this.searchQuery.set('');
    this.selectedFlowTypes.set([]);
    this.selectedTags.set([]);
    this.tagsFilterControl.setValue([], { emitEvent: false });
    this.selectedCameras.set({});
  }

  private captureUiState(): IntelligenceModels.AlertConfigurations.UiState {
    return {
      query: this.searchQuery(),
      cameras:
        this.selectedCameras() as IntelligenceModels.AlertConfigurations.UiState['cameras'],
      flowTypes: this.selectedFlowTypes(),
      tagIds: this.selectedTags(),
      scrollOffset:
        this.viewport()?.measureScrollOffset() ??
        this.response()?.uiState?.scrollOffset ??
        0,
    };
  }

  private restoreUiState(): void {
    const state = this.response()?.uiState;
    if (!state) return;
    this.searchQuery.set(state.query);
    this.selectedCameras.set(
      state.cameras as Dictionary<CameraPickerModels.SelectedCamera>,
    );
    this.selectedFlowTypes.set(state.flowTypes);
    this.selectedTags.set(state.tagIds);
    this.tagsFilterControl.setValue(state.tagIds, { emitEvent: false });
    setTimeout(() => this.viewport()?.scrollToOffset(state.scrollOffset));
  }

  // ── Row actions ──────────────────────────────────────────────────────────────

  protected onRowClick(config: EventV2Document): void {
    void this.openEdit(config);
  }

  protected onEdit(config: EventV2Document): void {
    void this.openEdit(config);
  }

  private async openEdit(config: EventV2Document): Promise<void> {
    const id = config._id;
    if (!id || this.editingId() || this.deletingId() || this.togglingId())
      return;
    const requestId = ++this.editRequestId;
    const entryPosition = this.resultsService.currentPosition();
    this.editingId.set(id);
    try {
      const fullConfiguration =
        await this.toolsService.fetchAlertConfiguration(id);
      if (
        requestId !== this.editRequestId ||
        entryPosition !== this.resultsService.currentPosition() ||
        this.resultsService.current()?.type !== 'alert-configurations'
      ) {
        return;
      }
      const response = this.response();
      if (!response) return;
      const retainedResponse = { ...response, uiState: this.captureUiState() };
      this.resultsService.replaceAlertConfigurationsInCurrent(retainedResponse);
      this.resultsService.setOverlay(
        {
          template: {
            category: fullConfiguration.selectedFlow.category,
            flowType: fullConfiguration.selectedFlow
              .flowType as FlowLookup['flowType'],
          },
          configuration: fullConfiguration,
        },
        'create-alert',
        this.translateService.instant(
          'SEARCH_GLOBAL_MODAL.ALERT_CONFIGURATIONS.EDIT_ALERT',
        ),
      );
    } catch (err) {
      if (requestId === this.editRequestId) {
        this.store$.dispatch(
          SharedActions.showMessage({
            error: this.utilsService.errMessage(err as Error),
          }),
        );
      }
    } finally {
      if (requestId === this.editRequestId) this.editingId.set(null);
    }
  }

  /** Enable/disable the configuration via the event-operations API. */
  protected onToggle(config: EventV2Document, event: Event): void {
    const id = config._id;
    if (!id || this.togglingId() || this.editingId()) return;
    const enabled = (event.target as HTMLInputElement).checked;
    this.togglingId.set(id);
    this.toolsService
      .toggleAlertConfiguration(config, enabled)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resultsService.updateAlertConfigurationInCurrent(id, {
            enabled,
          });
          this.togglingId.set(null);
        },
        error: (err) => {
          this.togglingId.set(null);
          // Force the toggle back to its stored state on failure.
          this.resultsService.updateAlertConfigurationInCurrent(id, {
            enabled: config.enabled,
          });
          this.store$.dispatch(
            SharedActions.showMessage({
              error: this.utilsService.errMessage(err),
            }),
          );
        },
      });
  }

  /** Confirm, then delete the configuration; swaps the row menu icon for a loader while in flight. */
  protected deleteConfig(config: EventV2Document): void {
    const id = config._id;
    if (!id || this.deletingId() || this.editingId()) return;
    this.confirmDialog
      .open({
        type: ConfirmDialogModel.ConfirmDialogType.CONFIRM,
        title: 'CONFIRM_MODALS.DELETE_ALERT.TITLE',
        msg: 'ALERT_LIST.DELETE_ALERT_CONFIRM',
        confirm: 'CONFIRM_MODALS.COMMON_YES',
        disableClose: true,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res?.selection !== ConfirmDialogModel.ConfirmResult.OK) return;
        this.deletingId.set(id);
        this.toolsService
          .deleteAlertConfiguration(config)
          .pipe(take(1), takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.resultsService.removeAlertConfigurationFromCurrent(id);
              this.deletingId.set(null);
            },
            error: (err) => {
              this.deletingId.set(null);
              this.store$.dispatch(
                SharedActions.showMessage({
                  error: this.utilsService.errMessage(err),
                }),
              );
            },
          });
      });
  }

  protected trackByConfigId(_index: number, config: EventV2Document): string {
    return config._id ?? String(_index);
  }

  // ── Sync-status helpers (copied verbatim from alert-main-v2 — pure, store-free) ─

  protected statusFor(config: EventV2Document): AlertSyncStatus {
    if (this.isCloudAlert(config)) {
      return AlertSyncStatus.synced;
    }
    if (config.cameraSync) {
      return this.syncStatus(config.cameraSync);
    }
    return config.synced
      ? AlertSyncStatus.synced
      : config.deleted
        ? AlertSyncStatus.deleted
        : AlertSyncStatus.notSynced;
  }

  private isCloudAlert(event: EventV2Document): boolean {
    return (
      (event.selectedFlow?.category === AlertCategory.Status &&
        event.selectedFlow?.flowType === StatusType.Edge) ||
      (event.selectedFlow?.category === AlertCategory.Integrations &&
        event.selectedFlow?.flowType === IntegrationsType.EventTag) ||
      (event.selectedFlow?.category === AlertCategory.Integrations &&
        event.selectedFlow?.flowType === IntegrationsType.EventTagPosValidation)
    );
  }

  private syncStatus(cameraSync: EventCameraSyncMap): AlertSyncStatus {
    const numOfCameras = Object.keys(cameraSync)?.length;
    const synced = Object.values(cameraSync).filter(
      (camera) => camera.synced,
    )?.length;
    if (synced === 0) {
      return AlertSyncStatus.notSynced;
    }
    if (synced !== numOfCameras) {
      return AlertSyncStatus.notHealthy;
    }
    return AlertSyncStatus.synced;
  }
}
